import { useEffect, useMemo, useRef, useState } from "react";

import styles from "../opsDashboard.module.css";
import type { OpsFilterOption } from "../opsDashboard.types";

interface Props {
    label: string;
    allLabel: string;
    options: OpsFilterOption[];
    selected: number[];
    isOpen: boolean;
    onToggleOpen: () => void;
    onToggleOption: (id: number) => void;
}

/**
 * Multi-select dropdown, per the mockup: a button showing the current selection,
 * opening a checkbox panel. Not a native <select> — the mockup's filters are
 * multi-select and the value line reads "All X" / the single pick / "N selected".
 *
 * Type-to-filter matches OrganizationMultiPicker, which is the searchable dropdown
 * everywhere else in the app: case-insensitive substring, the panel stays open while
 * picking, and selections survive the query changing under them. That last part is why
 * the checkbox list is filtered rather than replaced — a name typed, ticked, and then
 * searched past stays ticked, and the button still counts it.
 */
export function FilterSelect({
    label, allLabel, options, selected, isOpen, onToggleOpen, onToggleOption,
}: Props) {
    const ref = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const [query, setQuery] = useState("");

    useEffect(() => {
        if (!isOpen) return;
        const close = (ev: MouseEvent) => {
            if (ref.current && !ref.current.contains(ev.target as Node)) onToggleOpen();
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [isOpen, onToggleOpen]);

    useEffect(() => {
        // A stale query would silently hide options the next time the panel is opened.
        if (!isOpen) setQuery("");
        else searchRef.current?.focus();
    }, [isOpen]);

    const matches = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;

        // Keep a match's ANCESTORS, so a hit on a sub-org still renders under the parent
        // it belongs to rather than floating unattached. Same rule OrganizationMultiPicker
        // applies. A flat list has no depth, so this reduces to a plain filter there.
        const keep = new Set<number>();
        options.forEach((o, i) => {
            if (!o.label.toLowerCase().includes(q)) return;
            keep.add(o.id);
            let depth = o.depth ?? 0;
            for (let j = i - 1; j >= 0 && depth > 0; j--) {
                const above = options[j].depth ?? 0;
                if (above < depth) {
                    keep.add(options[j].id);
                    depth = above;
                }
            }
        });
        return options.filter((o) => keep.has(o.id));
    }, [options, query]);

    const value =
        selected.length === 0
            ? allLabel
            : selected.length === 1
              ? (options.find((o) => o.id === selected[0])?.label ?? allLabel)
              : `${selected.length} selected`;

    return (
        <div className={styles.filterField} ref={ref}>
            <button
                type="button"
                aria-label={label}
                aria-expanded={isOpen}
                className={[
                    styles.filterBtn,
                    isOpen ? styles.filterBtnOpen : "",
                    selected.length ? styles.filterBtnPicked : "",
                ].join(" ")}
                onClick={onToggleOpen}
            >
                <span className={styles.filterValue}>{value}</span>
                <span className={styles.filterCaret}>▾</span>
            </button>

            {isOpen && (
                <div className={styles.filterPanel}>
                    <input
                        ref={searchRef}
                        type="text"
                        className={styles.filterSearch}
                        // The button beside it already names the dimension, so repeating
                        // it here would be the third time the word appears in one control.
                        placeholder="Search…"
                        aria-label={`Search ${label}`}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") onToggleOpen();
                        }}
                    />

                    <div className={styles.filterOptions}>
                        {matches.map((o) => (
                            <label
                                key={o.id}
                                className={styles.filterOption}
                                // Organizations are a tree: campaigns hang off the
                                // operating sub-org while the name an operator reaches
                                // for is the system above it. Indenting shows which is
                                // which; picking a parent matches its descendants.
                                style={o.depth ? { paddingLeft: 12 + o.depth * 16 } : undefined}
                            >
                                <input
                                    type="checkbox"
                                    checked={selected.includes(o.id)}
                                    onChange={() => onToggleOption(o.id)}
                                />
                                {o.label}
                            </label>
                        ))}

                        {matches.length === 0 && (
                            <p className={styles.filterEmpty}>No matches</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
